// ============================================================
// BETTING RECOMMENDER TYPES
// ============================================================

import { GameState, getTotalPotAmount } from '@ppwr/poker-engine'
import { Doc } from '../_generated/dataModel'
import { ActionType } from './types'
import { normalizePots } from './adapters'

export type BetHintKind = 'allin' | 'fractionofpot' | 'multiplesofbigblind' | 'multiplesoflastbet'

export type BetHint = {
  amount: number
  kind: BetHintKind
  label: string
}

type BetRecommendationValue = number
type BetRecommendationCopy = string
type BetValueInfo = [BetRecommendationValue, BetRecommendationCopy]

// ============================================================
// BETTING CALCULATION HELPERS
// ============================================================

export function hasPlayersOnlyCalledBB(game: Doc<'games'>) {
  return game.currentHandActions.filter(Boolean).every((b) => ['call', 'blind'].includes(b.action))
}

/**
 * Get the last bet amount (for raises)
 */
export function getLastBet(game: Doc<'games'>): number {
  const currentRoundActions = game.currentHandActions.filter((a) => a.boardState === game.boardState)

  // Find the most recent bet or raise
  const lastBetAction = currentRoundActions
    .filter((a) => a.action === 'bet' || a.action === 'raise')
    .sort((a, b) => b.timestamp - a.timestamp)[0]

  if (lastBetAction && 'amount' in lastBetAction) {
    return lastBetAction.amount
  }

  // If no bets yet and we're in deal phase, return big blind
  if (game.boardState === 'deal') {
    return game.bigBlind
  }

  return game.bigBlind // Default to big blind
}

/**
 * Check if there are any bets (not just checks)
 */
export function hasBetsNoChecks(game: Doc<'games'>): boolean {
  const currentRoundActions = game.currentHandActions.filter((a) => a.boardState === game.boardState)

  return currentRoundActions.some((a) => a.action === 'bet' || a.action === 'raise' || a.action === 'allin')
}

// ============================================================
// BETTING RECOMMENDER
// ============================================================

/**
 * Betting recommendation multipliers and labels
 */
const MULTIPLIERS_OF_BLIND: BetValueInfo[] = [
  [2, '2x Big Blind'],
  [3, '3x Big Blind'],
  [4, '4x Big Blind']
]

const MULTIPLIERS_OF_BET: BetValueInfo[] = [
  [2, '2x Last Bet'],
  [3, '3x Last Bet'],
  [4, '4x Last Bet']
]

const FRACTIONS_OF_POT: BetValueInfo[] = [
  [1 / 3, '1/3 Pot'],
  [0.5, '1/2 Pot'],
  [0.75, '3/4 Pot'],
  [1, 'Full Pot']
]

/**
 * Get multiples of the big blind
 * Used in pre-flop betting when no bets have been made
 */
function getMultiplesOfBigBlind(game: Doc<'games'>): BetHint[] {
  return MULTIPLIERS_OF_BLIND.map(([multiplier, label]) => ({
    kind: 'multiplesofbigblind' as const,
    amount: Math.floor(multiplier * game.bigBlind),
    label
  }))
}

/**
 * Get multiples of the last bet
 * Used when there are existing bets to raise
 */
function getMultiplesOfLastBet(game: Doc<'games'>): BetHint[] {
  const lastBet = getLastBet(game)
  return MULTIPLIERS_OF_BET.map(([multiplier, label]) => ({
    kind: 'multiplesoflastbet' as const,
    amount: Math.floor(multiplier * lastBet),
    label
  }))
}

/**
 * Get fractions of the pot
 * Used post-flop when no bets have been made
 */
function getFractionsOfPot(game: Doc<'games'>): BetHint[] {
  const totalPot = getTotalPotAmount(normalizePots(game.pots))
  return FRACTIONS_OF_POT.map(([fraction, label]) => ({
    kind: 'fractionofpot' as const,
    amount: Math.floor(fraction * totalPot),
    label
  })).filter((betHint) => betHint.amount >= game.bigBlind) // Filter out hints smaller than big blind
}

/**
 * Get bet hints for before any action (pre-flop)
 * If there are bets, show multiples of last bet
 * Otherwise, show multiples of big blind
 */
function getHintsBeforeAction(game: Doc<'games'>): BetHint[] {
  if (hasBetsNoChecks(game)) {
    return getMultiplesOfLastBet(game)
  }
  return getMultiplesOfBigBlind(game)
}

/**
 * Get bet hints after board action (flop/turn/river)
 * If there are bets, show multiples of last bet
 * Otherwise, show fractions of pot
 */
function getBetsAfterAction(game: Doc<'games'>): BetHint[] {
  if (hasBetsNoChecks(game)) {
    return getMultiplesOfLastBet(game)
  }
  return getFractionsOfPot(game)
}

/**
 * Get betting recommendations based on game state
 *
 * @param game - The game document
 * @returns Array of bet hints with amounts, kinds, and labels
 *
 * @example
 * ```ts
 * const hints = getBetHints(game)
 * // => [
 * //   { amount: 200, kind: 'multiplesofbigblind', label: '2x Big Blind' },
 * //   { amount: 300, kind: 'multiplesofbigblind', label: '3x Big Blind' },
 * //   { amount: 400, kind: 'multiplesofbigblind', label: '4x Big Blind' }
 * // ]
 * ```
 */
export function getBetHints(game: Doc<'games'>): BetHint[] {
  // No hints in pre-deal or showdown
  if (game.boardState === 'predeal' || game.boardState === 'showdown' || game.boardState === 'hand_complete') {
    return []
  }

  // Pre-flop betting (deal phase)
  if (game.boardState === 'deal') {
    return getHintsBeforeAction(game)
  }

  // Post-flop betting (flop, turn, river)
  return getBetsAfterAction(game)
}

type ValidFxInputs = {
  bigBlind: number
  biggestBet: number
  playerAlreadyBet: number
  playerChips: number
  betAmount: number
  callAmount: number
  minimumRaise: number | undefined
}

const BetValidationErrors = {
  minimunRaiseError: 'minimunRaiseError',
  betLowerThanMinimunRaiseError: 'betLowerThanMinimunRaiseError',
  betBiggerThanChipStackError: 'betBiggerThanChipStackError',
  betLowerThanBigBlindError: 'betLowerThanBigBlindError',
  betLowerThanBiggestBetError: 'betLowerThanBiggestBetError',
  betIsNotCallAmount: 'betIsNotCallAmount'
} as const
type BetValidationError = keyof typeof BetValidationErrors

type ValidFxResult =
  | {
      ok: true
    }
  | {
      ok: false
      error: BetValidationError
      info: Record<string, unknown>
    }

/**
 * Returns true if the betAmount is a valid bet for this player on this
 * board. Returns false otherwise
 */
export function isValidBetForPlayer(inputs: ValidFxInputs, kind: ActionType) {
  // Bet Kinds that don't need validations
  //if ([BetKind.SMALLBLIND, BetKind.BIGBLIND, BetKind.BLANK].includes(kind)) return { ok: true as const }

  // All in is always a valid bet for a player
  if (inputs.betAmount === inputs.playerAlreadyBet + inputs.playerChips) return { ok: true as const }

  const validations = Validations[kind]

  const validationResults = validations.map((validFx) => validFx(inputs))

  const firstError = validationResults.find((result) => !result.ok)
  if (firstError && !firstError.ok) return firstError
  return { ok: true as const }
}

const Validations: Record<ActionType, ((inputs: ValidFxInputs) => ValidFxResult)[]> = {
  allin: [betAmountIsLessThanPlayersTotalChips],
  call: [betAmountIsLessThanPlayersTotalChips, betAmountIsCallAmount],
  bet: [
    betAmountIsGreaterThanMinimumRaise,
    betAmountIsLessThanPlayersTotalChips,
    betAmountIsAsBigAsBigBlindBet,
    betAmountIsAsBigAsBiggestBet
  ],
  raise: [
    betAmountIsGreaterThanMinimumRaise,
    betAmountIsLessThanPlayersTotalChips,
    betAmountIsAsBigAsBigBlindBet,
    betAmountIsAsBigAsBiggestBet
  ],
  fold: [],
  check: [],
  blind: [] // Blind posts don't need validation (forced action)
}

function betAmountIsLessThanPlayersTotalChips({
  playerAlreadyBet,
  playerChips,
  betAmount
}: ValidFxInputs): ValidFxResult {
  if (betAmount <= playerAlreadyBet + playerChips) {
    return { ok: true as const }
  }
  console.error(
    `${BetValidationErrors.betBiggerThanChipStackError} bet=${betAmount} chips=${playerAlreadyBet + playerChips}`
  )
  return {
    ok: false as const,
    error: BetValidationErrors.betBiggerThanChipStackError,
    info: { betAmount, playerChips, playerAlreadyBet }
  }
}

function betAmountIsCallAmount({ betAmount, callAmount }: ValidFxInputs): ValidFxResult {
  if (betAmount === callAmount) {
    return { ok: true as const }
  }
  console.error(`${BetValidationErrors.betIsNotCallAmount} ${betAmount}`)
  return { ok: false as const, error: BetValidationErrors.betIsNotCallAmount, info: { betAmount, callAmount } }
}

function betAmountIsGreaterThanMinimumRaise({
  betAmount,
  minimumRaise,
  playerChips,
  playerAlreadyBet
}: ValidFxInputs): ValidFxResult {
  if (!minimumRaise && minimumRaise !== 0) {
    return { ok: false, error: BetValidationErrors.minimunRaiseError, info: {} }
  }
  if (betAmount + playerAlreadyBet >= minimumRaise) return { ok: true as const }
  if (betAmount === playerChips) return { ok: true as const }

  console.error(`${BetValidationErrors.betLowerThanMinimunRaiseError} ${minimumRaise}`)
  return {
    ok: false as const,
    error: BetValidationErrors.betLowerThanMinimunRaiseError,
    info: { betAmount, minimumRaise, playerChips, playerAlreadyBet }
  }
}

function betAmountIsAsBigAsBigBlindBet({
  bigBlind,
  playerChips,
  betAmount,
  playerAlreadyBet
}: ValidFxInputs): ValidFxResult {
  if (playerChips >= bigBlind && betAmount + playerAlreadyBet >= bigBlind) {
    return { ok: true as const }
  }
  console.error(`${BetValidationErrors.betLowerThanBigBlindError} ${bigBlind}`)
  return {
    ok: false as const,
    error: BetValidationErrors.betLowerThanBigBlindError,
    info: { bigBlind, betAmount, playerChips, playerAlreadyBet }
  }
}

function betAmountIsAsBigAsBiggestBet({
  biggestBet,
  betAmount,
  playerChips,
  playerAlreadyBet
}: ValidFxInputs): ValidFxResult {
  if (biggestBet <= 0) return { ok: true as const }
  if (betAmount === playerChips) return { ok: true as const }
  if (betAmount + playerAlreadyBet >= biggestBet) return { ok: true as const }

  console.error(`${BetValidationErrors.betLowerThanBiggestBetError} ${biggestBet}`)
  return {
    ok: false as const,
    error: BetValidationErrors.betLowerThanBiggestBetError,
    info: { biggestBet, betAmount, playerChips, playerAlreadyBet }
  }
}
